import { WorkflowNode, WorkflowEdge } from "lib/types/workflow"

import {
  DROP_THRESHOLD,
  HANDLE_X_PCT,
  NODE_H,
  NODE_W,
  TERMINAL_Y_OFFSET,
  TerminalTarget
} from "../constants"

export const getHandleOffset = (
  nodeType: string | undefined,
  handleId: string
): number => {
  const pct = HANDLE_X_PCT[nodeType ?? ""]?.[handleId] ?? 0.5
  return NODE_W * pct
}

export const distToSegment = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number => {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(px - ax, py - ay)
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

export const findClosestEdge = (
  dragPos: { x: number; y: number },
  draggedId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): string | null => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  let closestId: string | null = null
  let closestDist = DROP_THRESHOLD

  const cx = dragPos.x + NODE_W / 2
  const cy = dragPos.y + NODE_H / 2

  for (const edge of edges) {
    if (edge.source === draggedId || edge.target === draggedId) continue

    const src = nodeMap.get(edge.source)
    const tgt = nodeMap.get(edge.target)
    if (!src || !tgt) continue

    const dist = distToSegment(
      cx,
      cy,
      src.position.x + NODE_W / 2,
      src.position.y + NODE_H / 2,
      tgt.position.x + NODE_W / 2,
      tgt.position.y + NODE_H / 2
    )

    if (dist < closestDist) {
      closestDist = dist
      closestId = edge.id
    }
  }

  return closestId
}

export const findClosestTerminal = (
  dragPos: { x: number; y: number },
  draggedId: string,
  nodes: WorkflowNode[],
  terminalHandlesByNode: Map<string, string[]>
): TerminalTarget | null => {
  const cx = dragPos.x + NODE_W / 2
  const cy = dragPos.y + NODE_H / 2
  let closest: TerminalTarget | null = null
  let closestDist = DROP_THRESHOLD

  for (const node of nodes) {
    if (node.id === draggedId) continue
    const handles = terminalHandlesByNode.get(node.id) ?? []
    for (const handleId of handles) {
      const hx = node.position.x + getHandleOffset(node.type, handleId)
      const hy = node.position.y + TERMINAL_Y_OFFSET
      const dist = Math.hypot(cx - hx, cy - hy)
      if (dist < closestDist) {
        closestDist = dist
        closest = { nodeId: node.id, handleId }
      }
    }
  }

  return closest
}
